import { Fragment } from "react";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { radius } from "#app/theme/v2/radius";
import { spacing } from "#app/theme/v2/spacing";
import { Divider } from "#components/atoms/Divider/Divider";
import { Text } from "#components/atoms/Typographies/Text";
import {
  getImpactedOptimizationSubtitle,
  getImpactedOptimizationTitle,
} from "#modules/optimizations/domain/impactedOptimizationLabel";
import { Icon } from "#shared/icons/Icon";
import { Flex } from "#shared/view/components/Flex";
import type { ImpactedOptimization } from "shared";

type Props = {
  optimizations: ImpactedOptimization[];
  withWarningHeader?: boolean;
};

export const ImpactedOptimizationList = ({ optimizations, withWarningHeader = false }: Props) => {
  const { t } = useTranslation();

  return (
    <Flex
      direction="column"
      gap={spacing[3]}
      p={spacing[4]}
      w="100%"
      style={{ borderRadius: radius.md, backgroundColor: colors.bg.elevation.secondary }}
    >
      {withWarningHeader && (
        <>
          <Flex gap={spacing[2]} align="flex-start">
            <Icon name="fillDanger" size={16} />
            <Flex direction="column">
              <Text size="small" type="medium">
                {t("optimizations.invalidation.title", { count: optimizations.length })}
              </Text>
              <Text size="small" color={colors.text.secondaryDefault}>
                {t("optimizations.invalidation.subtitle")}
              </Text>
            </Flex>
          </Flex>
          <Divider />
        </>
      )}
      {optimizations.map((optimization, index) => (
        <Fragment key={optimization.id}>
          {index > 0 && <Divider />}
          <Flex direction="column" gap={spacing[1]}>
            <Text size="small">{getImpactedOptimizationTitle(optimization, t)}</Text>
            <Text size="tiny" color={colors.text.secondaryDefault}>
              {getImpactedOptimizationSubtitle(optimization, t)}
            </Text>
          </Flex>
        </Fragment>
      ))}
    </Flex>
  );
};
